/**
 * LaTeX Linter for CodeMirror
 * Provides real-time linting using chktex or compilation errors
 */

import { linter, Diagnostic, lintGutter } from '@codemirror/lint'
import { EditorView } from '@codemirror/view'
import { latexAPI } from '@/api/latex'

// Cache for lint results to avoid excessive API calls
let lintCache: {
  content: string
  diagnostics: Diagnostic[]
  timestamp: number
} | null = null

const CACHE_DURATION = 2000 // 2 seconds
const DEBOUNCE_DELAY = 500 // 500ms debounce

let debounceTimer: NodeJS.Timeout | null = null

/**
 * LaTeX linter source for CodeMirror
 */
export function latexLinter() {
  return [
    // Add lint gutter to show markers in line number area
    lintGutter(),
    linter(async (view) => {
      const content = view.state.doc.toString()
      
      // Check cache first
      if (lintCache && lintCache.content === content) {
        const age = Date.now() - lintCache.timestamp
        if (age < CACHE_DURATION) {
          return lintCache.diagnostics
        }
      }
      
      // Debounce lint requests
      return new Promise<Diagnostic[]>((resolve) => {
        if (debounceTimer) {
          clearTimeout(debounceTimer)
        }
        
        debounceTimer = setTimeout(async () => {
          try {
            const response = await latexAPI.lintLatex(content)
            
            // Handle response - check if it has errors array
            if (!response || !response.errors) {
              resolve([])
              return
            }
            
            if (response.errors.length === 0) {
              resolve([])
              return
            }
            
            // Convert lint errors to CodeMirror diagnostics
            const diagnostics: Diagnostic[] = response.errors.map((error) => {
              // Convert 1-based line numbers to 0-based
              const line = Math.max(0, error.line - 1)
              const lineStart = view.state.doc.line(line + 1).from
              const pos = lineStart + Math.max(0, error.column - 1)
              
              return {
                from: pos,
                to: pos,
                severity: error.severity === 'error' ? 'error' : 'warning',
                message: error.message,
                actions: []
              }
            })
            
            // Update cache
            lintCache = {
              content,
              diagnostics,
              timestamp: Date.now()
            }
            
            resolve(diagnostics)
        } catch (error: unknown) {
          // Silently fail - return empty diagnostics on error (don't block editing)
          resolve([])
        }
        }, DEBOUNCE_DELAY)
      })
    }, {
      // Lint on every change, but debounced
      delay: DEBOUNCE_DELAY
    }),
  // Custom styling for lint markers (Overleaf-style)
  EditorView.theme({
    // Lint gutter container - ensure it's wide enough
    '.cm-lintGutter': {
      width: '24px',
      minWidth: '24px',
      paddingRight: '4px',
    },
    // Error markers (red circles) - in gutter - Overleaf style
    '.cm-lintGutter .cm-lint-marker-error': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '16px',
      height: '16px',
      minWidth: '16px',
      minHeight: '16px',
      borderRadius: '50%',
      backgroundColor: '#DC2626', // Red-600
      border: 'none',
      cursor: 'pointer',
      margin: '1px 2px',
      position: 'relative',
      flexShrink: 0,
      fontSize: '12px',
      lineHeight: '1',
      color: 'white',
      fontWeight: 'bold',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        backgroundColor: '#B91C1C', // Red-700
        transform: 'scale(1.15)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      },
      '&::after': {
        content: '"●"',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '10px',
        color: 'white',
        lineHeight: '1',
      }
    },
    // Warning markers (yellow/orange triangles) - in gutter - Overleaf style
    '.cm-lintGutter .cm-lint-marker-warning': {
      display: 'inline-block',
      width: '0',
      height: '0',
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '14px solid #F59E0B', // Amber-500
      cursor: 'pointer',
      margin: '1px 2px',
      position: 'relative',
      flexShrink: 0,
      backgroundColor: 'transparent',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        borderBottomColor: '#D97706', // Amber-600
        transform: 'scale(1.15)',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      }
    },
    // Error markers in content (underlines)
    '.cm-lint-marker-error': {
      color: '#DC2626',
      cursor: 'pointer',
      textDecoration: 'underline',
      textDecorationColor: '#DC2626',
      textDecorationStyle: 'wavy',
      textDecorationThickness: '2px',
    },
    // Warning markers in content (underlines)
    '.cm-lint-marker-warning': {
      color: '#F59E0B',
      cursor: 'pointer',
      textDecoration: 'underline',
      textDecorationColor: '#F59E0B',
      textDecorationStyle: 'wavy',
      textDecorationThickness: '2px',
    },
    // Lint tooltip/popup styling
    '.cm-lintRange': {
      backgroundColor: 'transparent',
    },
    '.cm-lintRange-error': {
      backgroundColor: 'rgba(220, 38, 38, 0.1)', // Red with opacity
    },
    '.cm-lintRange-warning': {
      backgroundColor: 'rgba(245, 158, 11, 0.1)', // Amber with opacity
    },
    // Dark mode support
    '.dark .cm-lintGutter .cm-lint-marker-error': {
      backgroundColor: '#EF4444', // Red-500
      borderColor: '#EF4444',
      '&:hover': {
        backgroundColor: '#DC2626', // Red-600
        borderColor: '#DC2626',
      }
    },
    '.dark .cm-lintGutter .cm-lint-marker-warning': {
      borderBottomColor: '#FBBF24', // Amber-400
      '&:hover': {
        borderBottomColor: '#F59E0B', // Amber-500
      }
    },
    '.dark .cm-lint-marker-error': {
      color: '#EF4444',
      textDecorationColor: '#EF4444',
    },
    '.dark .cm-lint-marker-warning': {
      color: '#FBBF24',
      textDecorationColor: '#FBBF24',
    },
    '.dark .cm-lintRange-error': {
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
    },
    '.dark .cm-lintRange-warning': {
      backgroundColor: 'rgba(251, 191, 36, 0.15)',
    },
  })
  ]
}

